import { formatDate } from 'date-fns';
import { CHART } from './constants';
import type { useMultipleNarrativeValuationHistory } from './hooks/useNarrativeValuationHistory';
import type { HoverParam } from './components/ChartOverlay';
import type { Narrative } from './hooks/useNarrativeChart';
import type { ScaleLinear, ScaleTime } from 'd3-scale';

// TODO: Duplicate into this project structure to prepare for Next.js migration
import { formatterCurrency } from '@/utilities/format';

type BaselineType = 'initial' | 'current' | 'average';

export interface NormalisedDataPoint {
  originalValue: number | null;
  value: number;
}

interface NormalisedValuation {
  originalFairValue: number | null;
  fairValue: number | null;
  createdAt: string;
}

interface NormalisedMultipleNarrativeChartData {
  normalisedEntities: Record<number, NormalisedDataPoint>;
  normalisedValuationsByNarrative: (NormalisedValuation[] | undefined)[];
}

export const normalizeMultipleNarrativeChartData = (
  entities: Record<number, number | null>,
  keys: number[],
  narratives: Narrative[],
  baselineType: BaselineType = 'initial'
): NormalisedMultipleNarrativeChartData => {
  // Choose reference price (baseline) based on user preference
  let baseline = 0;

  switch (baselineType) {
    case 'initial':
      baseline = entities[keys[0]] || 1; // Use first price as 100%
      break;
    case 'current':
      baseline = entities[keys[keys.length - 1]] || 1; // Use latest price as 100%
      break;
    case 'average':
      baseline =
        keys.reduce((sum, key) => sum + (entities[key] || 0), 0) /
          keys.length || 1; // Use average as 100%
      break;
  }

  // Convert all share prices to percentage changes
  const normalisedEntities = {} as Record<number, NormalisedDataPoint>;

  keys.forEach((key) => {
    const value = entities[key];

    if (value !== null && value !== undefined && baseline !== 0) {
      normalisedEntities[key] = {
        originalValue: value,
        value: (value / baseline - 1) * 100,
      };
    } else {
      normalisedEntities[key] = {
        originalValue: value,
        value: 0,
      };
    }
  });

  // Convert fair values to percentage changes for each narrative
  const normalisedValuationsByNarrative = narratives.map((narrative) => {
    return narrative.valuations?.map((v) => ({
      ...v,
      originalFairValue: v.fairValue,
      fairValue:
        v.fairValue && baseline !== 0
          ? (v.fairValue / baseline - 1) * 100
          : null,
    }));
  });

  return { normalisedEntities, normalisedValuationsByNarrative };
};

export const clamp = (position: number, max: number, padding = 0): number =>
  Math.min(Math.max(position, 0), max - padding);

export const getSelectedData = ({
  selectedIndex,
  markerData,
  narratives,
  lastPrice,
  currencyISO,
  xScale,
  narrativeValuationHistories,
  yScale,
  chartData,
  firstPrice,
}: {
  selectedIndex?: number;
  markerData: HoverParam | null;
  narratives: Narrative[];
  lastPrice: NormalisedDataPoint | null;
  currencyISO: string;
  xScale: ScaleTime<number, number>;
  narrativeValuationHistories: ReturnType<
    typeof useMultipleNarrativeValuationHistory
  >;
  yScale: ScaleLinear<number, number>;
  chartData: ReturnType<typeof normalizeMultipleNarrativeChartData>;
  firstPrice: number;
}) => {
  const fairValueNumbers = (narratives ?? []).map(
    (narrative, index) =>
      markerData?.fairValues[index] ?? narrative?.valuation?.fairValue
  );
  const priceNumber = markerData?.price ?? lastPrice?.originalValue ?? 0;
  return {
    fairValueIndex: selectedIndex ?? 0,
    date: formatDate(
      new Date(
        markerData?.time ??
          (narratives ?? [])[0]?.updatedAt ??
          new Date().toISOString()
      ),
      CHART.TOOLTIP_DATE_FORMAT
    ),
    priceNumber,
    price: formatterCurrency(priceNumber, currencyISO),
    fairValues: fairValueNumbers.map((fv) =>
      fv ? formatterCurrency(fv, currencyISO) : null
    ),
    fairValueNumbers,
    x: markerData?.time ? xScale(new Date(markerData?.time).getTime()) : 0,
    getX: (d: number) => xScale(d || 0),
    getFairValueYs: narrativeValuationHistories.map(
      (history) => (d: number) =>
        yScale(history?.valuationsByDate[d]?.value || 0)
    ),
    getPriceY: (d: number) =>
      yScale(chartData.normalisedEntities[d]?.value || 0),
    priceChange: Math.abs(priceNumber - firstPrice).toFixed(2),
    percentageChange:
      firstPrice === 0
        ? '0.00%'
        : `${(((priceNumber - firstPrice) / firstPrice) * 100).toLocaleString(
            'en-US',
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}%`,
  } as const;
};

export const getLastData = ({
  selectedIndex,
  lastPrice,
  currencyISO,
  lastFairValues,
  yScale,
  xScale,
  lastPriceKey,
  firstPrice,
}: {
  selectedIndex?: number;
  lastPrice: NormalisedDataPoint | null;
  currencyISO: string;
  lastFairValues: (NormalisedDataPoint | null)[];
  yScale: ScaleLinear<number, number>;
  xScale: ScaleTime<number, number>;
  lastPriceKey: number;
  firstPrice: number;
}) => {
  const priceNumber = lastPrice?.originalValue ?? 0;
  return {
    fairValueIndex: selectedIndex ?? 0,
    price: formatterCurrency(priceNumber, currencyISO),
    fairValues: lastFairValues.map((fv) =>
      fv?.originalValue
        ? formatterCurrency(fv.originalValue, currencyISO)
        : null
    ),
    fairValueYs: lastFairValues.map((fv) => yScale(fv?.value ?? 0)),
    lastX: xScale(lastPriceKey) ?? 0,
    priceY: lastPrice?.value ? yScale(lastPrice?.value) : null,
    priceChange: Math.abs(priceNumber - firstPrice).toFixed(2),
    percentageChange:
      firstPrice === 0
        ? '0.00%'
        : `${(((priceNumber - firstPrice) / firstPrice) * 100).toLocaleString(
            'en-US',
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}%`,
  } as const;
};
